<template>
  <div v-if="backgroundImage.current">
    <Transition name="background-fade">
      <div
        v-if="staticImage && backgroundImageUrl"
        :key="backgroundImageUrl"
        class="backgroundContainer"
        ref="backgroundContainer"
        :style="{
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: 'cover'
        }"
      >
        <Transition name="background-fade" appear>
          <div v-if="backgroundBlur" class="backdropWrapper" :key="refreshBlur" />
        </Transition>
      </div>
    </Transition>
    <Transition name="background-fade">
      <div class="background-wrapper" v-if="!staticImage">
        <div class="background-container">
          <div ref="lottieContainer" class="lottie-animation" />
        </div>
        <Transition name="background-fade" appear>
          <div v-if="backgroundBlur" class="backdropWrapper" />
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import type { Ref } from 'vue'
import { storeToRefs } from 'pinia'
import lottie from 'lottie-web'

// Types
import type { IntroOutroAnimation, AnimationTimeStamp, ManagedTimeout } from '@/customTypes'

// Stores
import { useDataStore } from '@/stores/DataStore'
import { useEffectsStore } from '@/stores/EffectsStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { usePreloadStore } from '@/stores/PreloadStore'

// Helpers
import { useTimeout } from '@/utils/helpers'

// Services
import AxiosInstance from '@/services/AxiosInstance.js'

import Logger from '@/services/Logger.js'

const logger: any = new Logger('BackgroundComponent', true)

const props = defineProps({
  currentScene: {
    type: String
  },
  backgroundTrackVolume: {
    type: Number,
    required: true
  }
})

const backgroundTrackVolume = computed(() => props.backgroundTrackVolume)
const currentScene = computed(() => props.currentScene)
let previousScene: string | null = null
let previousBackground: string | null = null

const soundEffectsStore = useSoundEffectsStore()
const dataStore = useDataStore()
const { currentSubtest, screenerTemplateId, backgroundVolumeFadeLevel } = storeToRefs(dataStore)
const effectsStore = useEffectsStore()
const { backgroundAnimationState, backgroundImage, backgroundBlur } = storeToRefs(effectsStore)
const preloadStore = usePreloadStore()
const { setManagedTimeout, clearManagedTimeout } = useTimeout()

let anim: any = null
const backgroundTransitionDuration = ref<number>(1000)
const lottieContainer: Ref<HTMLDivElement | null> = ref(null)
const currentAnimation: Ref<AnimationTimeStamp | null> = ref(null)
const awaitingAnimation: Ref<Function | null> = ref(null)
const loadingAnimation: Ref<boolean> = ref(false)
const refreshBlur: Ref<number> = ref(0)
const resizeTimeout: Ref<ManagedTimeout | null> = ref(null)

// NOTE: Computed
const staticImage = computed(() => {
  if (typeof backgroundImage.value.current === 'string' && backgroundImage.value.current !== '') {
    const lastDotIndex = backgroundImage.value.current.lastIndexOf('.')
    if (lastDotIndex !== -1) {
      const extension = backgroundImage.value.current.substring(lastDotIndex + 1)
      if (extension !== 'json') {
        removeBGAnimation()
        return true
      }
      loadLottieBackground()
      return false
    } else return true
  } else return true
})

const backgroundImageUrl = computed(() => {
  return backgroundImage.value.current &&
    (backgroundImage.value.current as string).toLowerCase() !== 'blank'
    ? `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/subtest-backgrounds/${backgroundImage.value.current}?v=${_ASSETS_VERSION_}`
    : ''
})

// NOTE: Lifecycle
onMounted(async () => {
  logger.info('background image on mounted', backgroundImageUrl.value)
  if (!staticImage.value) loadLottieBackground()
  window.addEventListener('resize', () => handleBlurWindowResize)
})

onUnmounted(() => {
  removeBGAnimation()
  logger.info('background image unmounted', backgroundImageUrl.value)
  const audioTrack = `${currentScene.value}_audio`
  soundEffectsStore.stopTrack(audioTrack)
  window.removeEventListener('resize', () => handleBlurWindowResize)
})

// NOTE: Watchers

watch(
  backgroundBlur,
  (blur: boolean) => {
    if (!blur) {
      document.body.style.overflow = 'auto'
    }
  },
  { immediate: true }
)

watch(backgroundAnimationState, (animation: IntroOutroAnimation) => {
  if ((currentAnimation.value && currentAnimation.value.animation === animation) || !animation)
    return

  if (loadingAnimation.value) {
    awaitingAnimation.value = () => playAnimationSegment(animation)
  } else {
    playAnimationSegment(animation)
  }
})

//On scene change where a background transition has taken place, update the background track
watch(backgroundImageUrl, () => {
  logger.info('Current scene is:', currentScene.value, 'Prev ', previousScene)
  changeBackgroundMusic()
})

// On scene change with no background change, update the background track
watch(
  currentScene,
  () => {
    if (!currentScene.value) return
    if (
      backgroundImage.value.current === backgroundImage.value.next &&
      backgroundImage.value.current !== '' &&
      backgroundImage.value.next !== ''
    ) {
      changeBackgroundMusic()
    }
  },
  { immediate: true }
)

// On volume change, adjust the background track volume
watch(backgroundTrackVolume, (volume: number) => {
  const audioTrack = `${currentScene.value}_audio`
  soundEffectsStore.adjustVolume(audioTrack, volume, true)
})

// NOTE: Methods
const loadLottieBackground = async () => {
  const lottiePath = backgroundImageUrl.value.substring(
    0,
    backgroundImageUrl.value.lastIndexOf('/') + 1
  )
  const backgroundLottieData = await AxiosInstance.get(backgroundImageUrl.value)

  // Updates lottie files to be cache versioning
  if (backgroundLottieData.assets) {
    for (let asset of backgroundLottieData.assets) {
      if (asset.p) {
        asset.u = `${lottiePath}${asset.u}`
        asset.p = `${asset.p}?v=${_ASSETS_VERSION_}`
      }
    }
  }

  try {
    if (lottieContainer.value && anim === null) {
      anim = lottie.loadAnimation({
        container: lottieContainer.value,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        animationData: backgroundLottieData
      })
      loadingAnimation.value = true

      anim.addEventListener('DOMLoaded', onAnimationLoaded)
    }
  } catch (error) {
    logger.error('Failed to load animation', error)
  }
}

const onAnimationLoaded = () => {
  loadingAnimation.value = false
  if (awaitingAnimation.value) {
    awaitingAnimation.value()
  }
}

const playAnimationSegment = (animation: string) => {
  const nextAnimation = effectsStore.getAnimation(animation, 'introOutro')
  if (!nextAnimation) {
    logger.error(`Animation "${animation}" not found`)
    return
  }
  currentAnimation.value = nextAnimation
  logger.info('currentAnimation', currentAnimation.value)
  anim.playSegments([nextAnimation.start, nextAnimation.end], true)
  if (nextAnimation.audio) {
    soundEffectsStore.playTrack(nextAnimation.animation)
  }
  effectsStore.setBackgroundAnimationState(null)
}

const removeBGAnimation = () => {
  if (anim) {
    anim.removeEventListener('DOMLoaded', onAnimationLoaded)
    anim?.destroy()
    anim = null
  }
}

const changeBackgroundMusic = () => {
  if (previousScene && previousScene !== currentScene.value) {
    const audioTrack = `${previousScene}_audio`
    soundEffectsStore.stopTrack(audioTrack, true)
    if (staticImage.value && previousBackground && previousScene.match(/^S\d{1,2}/)) {
      preloadStore.deleteImageFromCache(previousBackground)
    }
  }
  if (!currentScene.value) return
  const audioTrack = `${currentScene.value}_audio`
  logger.info('Attempting to start current scene audio:', audioTrack)
  if (backgroundTrackVolume.value === backgroundVolumeFadeLevel.value) {
    soundEffectsStore.playTrack(audioTrack, backgroundVolumeFadeLevel.value)
  } else {
    soundEffectsStore.playTrack(audioTrack, true)
  }
  previousScene = currentScene.value as string
  previousBackground = backgroundImage.value.current
}

const handleBlurWindowResize = () => {
  backgroundTransitionDuration.value = 0
  refreshBlur.value++
  if (resizeTimeout.value) clearManagedTimeout(resizeTimeout)

  resizeTimeout.value = setManagedTimeout(() => {
    backgroundTransitionDuration.value = 1000
  }, 300)
}
</script>

<style lang="scss" scoped>
.center-square {
  display: none !important;
}
.background-wrapper {
  width: var(--full-vw);
  height: var(--full-vh);
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  background-color: #ffffff;
}

.background-container {
  position: absolute;
  left: 50%;
  top: 50%;
  width: calc(var(--full-vw) * 2314 / 1357);
  height: calc(var(--full-vh) * 1640 / 998);
  transform: translate(-50%, -50%);
  min-width: var(--full-vw);
  min-height: var(--full-vh);
}

.lottie-animation {
  width: 100%;
  height: 100%;
}

.backgroundContainer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background-color: v-bind('currentSubtest?.metadata?.backgroundColour || "white"');
  background-repeat: no-repeat;
  background-position: bottom center;
  background-attachment: fixed;
  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  background-size: cover;
}

.backdropWrapper {
  position: relative;
  width: var(--full-vw);
  height: var(--full-vh);
  backface-visibility: hidden;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transform: translateZ(0); /* Trigger GPU Rendering to help avoid flickering */
}

.background-check {
  position: absolute;
  top: 20px;
  right: 100px;
  z-index: 1000;
}

.background-fade-enter-active,
.background-fade-leave-active {
  transition: opacity v-bind('`${backgroundTransitionDuration}ms`');
}

.background-fade-enter-from,
.background-fade-leave-to {
  opacity: 0;
}
</style>
